import type { RoomMember } from '@cube-rush/shared';
import { useApp } from '../../app/context';
import { ru } from '../../i18n/ru';
import type { VoicePeer } from '../../voice/types';
import './PeerVoice.css';

/** The steps of the per-player volume button: loud, quieter, silent. */
export const PEER_VOLUME_STEPS = [1, 0.5, 0] as const;

/** The step after the one closest to `volume` (silent wraps around to loud). */
export function nextPeerVolume(volume: number): number {
  let closest = 0;
  PEER_VOLUME_STEPS.forEach((step, i) => {
    if (Math.abs(step - volume) < Math.abs((PEER_VOLUME_STEPS[closest] ?? 1) - volume)) closest = i;
  });
  return PEER_VOLUME_STEPS[(closest + 1) % PEER_VOLUME_STEPS.length] ?? 1;
}

function usePeer(member: RoomMember): VoicePeer | null {
  const { voice } = useApp();
  if (!voice.enabled.value || !member.connected) return null;
  return voice.peers.value.find((peer) => peer.id === member.id) ?? null;
}

/**
 * Says when the voice of another player does not get through: only while voice is on and
 * that player has a microphone switched on (otherwise there is nothing to hear anyway).
 */
export function PeerVoiceBadge({ member }: { member: RoomMember }) {
  const peer = usePeer(member);
  if (!peer || member.muted || peer.state === 'connected' || peer.state === 'closed') return null;
  const lost = peer.state === 'failed' || peer.state === 'disconnected';
  return (
    <span class={`badge ${lost ? 'badge--offline' : 'badge--wait'}`}>
      {lost ? ru.voice.lost : ru.voice.connecting}
    </span>
  );
}

/**
 * How loud another player is: an ear with level bars ("how well I hear them", unlike the
 * microphone icon next to it, which is about their mic). A tap goes loud → quieter →
 * silent → loud.
 */
export function PeerVolumeButton({ member }: { member: RoomMember }) {
  const { voice } = useApp();
  const peer = usePeer(member);
  if (!peer) return null;
  const lit = peer.volume >= 0.75 ? 3 : peer.volume > 0 ? 2 : 0;
  return (
    <button
      type="button"
      class={`peer-volume${lit === 0 ? ' peer-volume--silent' : ''}`}
      aria-label={ru.voice.peerVolume(member.profile.name, Math.round(peer.volume * 100))}
      onClick={() => voice.setPeerVolume(member.id, nextPeerVolume(peer.volume))}
    >
      <span class="peer-volume__ear" aria-hidden="true">
        👂
      </span>
      <span class="peer-volume__bars" aria-hidden="true">
        {[1, 2, 3].map((bar) => (
          <span
            key={bar}
            class={`peer-volume__bar peer-volume__bar--${bar}${bar <= lit ? ' peer-volume__bar--on' : ''}`}
          />
        ))}
      </span>
    </button>
  );
}
